import type { EncodingStatus } from "./EncoderAPI.types";
import type { GeoblockMode, GeoCountry } from "./geoblock.types";
import type { CountryGroup } from "./OfferAPI.types";
import type { TypeIndexMedia } from "../constants/fast";
import type {
  AD_BREAK,
  MediaFormat,
  MediaFormatWithAdBreak,
  MediaPublicationStatus,
  MediaStatus,
  MediaType,
} from "../constants/media";
import type { PictureKeys } from "../constants/ratio";
import type { LiveStreamStatus, LiveStreamTypes } from "../models/LiveStream";
import type {
  Cuepoints,
  Indexes,
  KeyWithTranslation,
  Page,
  Picture,
  Pictures,
  Translation,
  TranslationsIndexes,
  TranslationsIndexesWithLanguage,
} from "../types";

export interface MediaFormatObject {
  key: MediaFormat;
  uuid: string;
}

export interface IndexFastMediaItem {
  uuid: string;
  updated_at: string;
  type: TypeIndexMedia;
  source_duration: number;
  label: string;
  format?: MediaFormatObject | typeof AD_BREAK;
  cuepoints?: Cuepoints;
  object_picture: string | null;
}

export interface GetFastIndexMediaListResponse {
  page: { number: number; total_items: number; total: number };
  data: IndexFastMediaItem[];
}

export type FormatFilter = IncludesFormatFilter | ExcludesFormatFilter;

export interface IncludesFormatFilter {
  includes: MediaFormatWithAdBreak[];
  excludes?: never;
}
export interface ExcludesFormatFilter {
  excludes: MediaFormatWithAdBreak[];
  includes?: never;
}
export interface DurationFilter {
  min: number;
  max: number;
}

export interface GetFastIndexMediaListParams {
  channelId: string;
  page?: number;
  perPage?: number;
  search?: string;
  filter?: {
    format?: FormatFilter;
    duration?: DurationFilter;
  };
  order?: { direction: "up" | "down"; field: string };
}

export const FAST_TYPE = {
  INTERNAL: "internal",
  EXTERNAL: "external",
};

export type FastType = (typeof FAST_TYPE)[keyof typeof FAST_TYPE];

export interface ReplayRange {
  tc_in: number;
  tc_out: number;
}

export interface LiveStreamData {
  instant_replay: boolean;
  live_end?: string | null;
  live_start?: string | null;
  manifest: Manifest;
  status: LiveStreamStatus;
  uuid: string;
}

export interface Manifest {
  cipher_iv: string;
  cipher_key: string;
  editor_m3u8: string;
  encoding_start: string;
  encoding_timestamp: string;
  input_type: LiveStreamTypes;
  live_id: string;
  m3u8: string;
  manifest_editor_id: string;
  manifest_id: string;
  manifest_replay_id: string;
  max_resolution: string;
  replay_cipher_iv: string;
  replay_cipher_key: string;
  replay_m3u8: string;
  replay_start_ms?: number;
  replay_end_ms?: number;
  stop_reason: string;
  stream_key: string;
  stream_url: string;
}

export interface TargetAudience {
  uuid: string;
}

export type MediaRightsMode = "simple" | "advanced";

export type MediaDistributionType = "storefront" | "fast" | "d2g";

export interface MediaRight {
  uuid?: string;
  distribution_type: MediaDistributionType;
  storefront_project_uuid?: string | null;
  fast_channel_uuid?: string | null;
  geoblock_mode: GeoblockMode;
  countries: string[];
  country_groups: string[];
  temporal_restriction: boolean;
  begin_date: string | null;
  end_date: string | null;
  commercial_restriction: boolean;
  commercial_restriction_begin_date: string | null;
  commercial_restriction_end_date: string | null;
  views_count: number | null;
}

export interface FastSchedulingConflictSlot {
  fast_channel_name: string;
  timezone: string;
  start_time: string;
  end_time: string;
}

export interface GetMediaResponse {
  vr: boolean;
  nationalities: Nationality[];
  is_deletable: boolean;
  metadata: unknown;
  isrc_code: unknown;
  custom_params: unknown;
  custom_id: unknown;
  themes: Format[];
  offers: MediaOffer[];
  is_replay: boolean;
  keywords: Category[];
  new_genre: Format;
  updated_at: string;
  medias_castings: MediaCasting[];
  duration: number;
  live_src_uuid: unknown;
  smartlists: (MediaSmartlist | MediaSmartlistSeason)[];
  offers_available: number;
  commercial_restriction_end_date: string | null;
  commercial_restriction: boolean;
  external_stream: {
    epg_url?: string;
    player_code?: string | null;
    type?: string;
    url?: string | null;
    url_type?: string | null;
    uuid?: string;
  } | null;
  fast_source: {
    broadcast_url: string;
    fast_channel_broadcast_uuid: string;
    name: string;
  } | null;
  fast: FastType;
  fast_asset: FastAsset;
  eidr_code: string | null;
  lang_availables: OriginalVer[];
  genres: Category[];
  production_date: string;
  geoblock_mode: GeoblockMode;
  rights_mode: MediaRightsMode;
  rights: MediaRight[];
  replay_uuid: string | null;
  child_classification: ChildClassification;
  internal_comment: unknown;
  embed_profile_uuid: string | null;
  label: string;
  streams_count: number;
  isan_code: string | null;
  hosted_streams: HostedStream[];
  inserted_at: string;
  is_editable: boolean;
  translations_indexes: TranslationsIndexes;
  can_replay: boolean;
  type: MediaType;
  translations: MediaTranslation[];
  target_audience?: TargetAudience;
  audio_only: boolean;
  trailer_media_uuid: string;
  master_media_uuid: string | null;
  copyright: Category | null;
  children: MediaChildren[];
  original_ver: OriginalVer | null;
  end_date: string | null;
  commercial_restriction_begin_date: string | null;
  format: Format;
  parental_rating: unknown;
  status: string;
  countries: GeoCountry[];
  country_groups?: CountryGroup[];
  is_archivable: boolean;
  temporal_restriction: boolean;
  source_medias: unknown[];
  publication_status: string;
  uuid: string;
  begin_date: string;
  duration_type: DurationType;
  medias_castings_indexes: MediasCastingsIndexes;
  category: Category;
  publication_date: unknown;
  comments: number;
  owner: Owner;
  replay_range: ReplayRange | null;
  live_stream: LiveStreamData | null;
  accessibilities: unknown;
  is_in_adbreak: boolean;
  cuepoints: MediaCuepoint[];
  source_duration_ms: number | null;
  isFetched: boolean;
  has_multiple_ads: boolean;
  freemium_end: string | null;
  freemium_end_ms: number | null;
  is_freemium: boolean;
}

export interface GetMediaListResponse {
  data: IndexMedia[];
  indexes: Indexes;
  page: Page;
  type: "media";
}

export type GetFreeMediaListResponse = GetMediaListResponse;

export interface GetMediaListParams {
  page?: number;
  perPage?: number;
  filter: Record<string, unknown>;
  order: { field: string; direction: string };
  search?: string | null;
}

export interface GetFreeMediaListParams {
  page?: number;
  perPage?: number;
  search?: string | null;
  order?: { field: string; direction: "up" | "down" };
}

export interface IndexMedia {
  advertisings_count: number;
  audio_only: boolean;
  begin_date: string;
  commercial_restriction: boolean;
  commercial_restriction_begin_date: null;
  commercial_restriction_end_date: null;
  cover_picture_16_9: string | null;
  duration: number;
  enabled_cuepoints: number;
  end_date: null;
  external_stream: null;
  geoblock_mode: GeoblockMode;
  rights_mode: MediaRightsMode;
  is_replay: boolean;
  label: string;
  master_media_uuid: string | null;
  offers_available: number;
  offers_count: number;
  owner: IndexMediaOwner;
  publication_reasons?: string[];
  publication_status: string;
  series_count: number;
  smartlists_count: number;
  status: string;
  streams_count: number;
  subtitles_count: number;
  temporal_restriction: boolean;
  total_cuepoints: number;
  trad_count: number;
  type: MediaType;
  updated_at: string;
  uuid: string;
  live_start?: string | null;
  live_end?: string | null;
  live_status?: LiveStreamStatus | null;
  duration_str?: string | null;
}

export interface MediaCuepoint {
  uuid: string;
  timecode: string;
  enabled: boolean;
}

export type MediaCuepointAdded = Omit<MediaCuepoint, "uuid">;

export interface MediaUpdated extends Omit<
  GetMediaResponse,
  "offers" | "smartlists" | "fast_source" | "cuepoints"
> {
  offers: (MediaOffer | MediaOfferAdded)[];
  smartlists: (MediaSmartlist | MediaSmartlistAdded | MediaSmartlistSeason)[];
  fast_source: {
    broadcast_url?: string;
    fast_channel_broadcast_uuid: string;
  } | null;
  cuepoints: (MediaCuepoint | MediaCuepointAdded)[];
}

export enum LanguagesEnum {
  Co = "co",
  De = "de",
  En = "en",
  Fr = "fr",
  It = "it",
  Pt = "pt",
  Ar = "ar",
}

export interface Format {
  uuid: string;
  translations_indexes: TranslationsIndexesWithLanguage;
  translations: FormatTranslation[];
  key: string;
}

export interface FormatTranslation {
  uuid: string;
  title: string;
  language_uuid: string;
  language: LanguagesEnum;
  default: boolean;
}

export interface HostedStream {
  uuid: string;
  type: string;
  status: string;
  source_media_uuid: string | null;
  source_media_stream: number;
  name: string;
  mandatory: boolean;
  language_uuid: string;
  encoding_template: EncodingTemplate;
  default: boolean;
}

export interface EncodingTemplate {
  uuid: string;
  template: Template;
  name: string;
}

export interface Template {
  max_frame_rate?: number;
  bitrate?: number;
  width?: number;
  every?: number;
}

export interface OriginalVer {
  uuid: string;
  translations_indexes: TranslationsIndexesWithLanguage;
  translations: FormatTranslation[];
  part1: string;
  id: string;
}

export interface Nationality {
  uuid: string;
  translations_indexes: TranslationsIndexesWithLanguage;
  translations: FormatTranslation[];
  alpha3: string;
  alpha2: string;
}

export interface MediaOffer {
  uuid: string;
  storefront_project_uuid: string;
  storefront_project_name: string;
  is_free: boolean;
  o_label: string;
  has_ads: boolean;
}

export type MediaOfferAdded = Omit<MediaOffer, "is_free" | "has_ads">;

export interface IndexMediaOwner {
  avatar: string;
  avatar_url: string;
  company: string;
  email: string;
  first_name: string;
  last_name: string;
  organization_uuid: string;
  uuid: string;
}

export interface Owner {
  uuid: string;
  organization_uuid: string;
  name: string;
  email: string;
  company: string;
  avatar_url: string;
  avatar: string;
}

export enum SmartlistType {
  AUTO = "auto",
  SEASON = "season",
  AUDIO = "audio",
  STANDARD = "standard",
  COLLECTION = "collection",
  SERIES = "series",
}

export interface MediaSmartlist {
  uuid: string;
  type: Exclude<SmartlistType, SmartlistType.SEASON>;
  storefront_project_uuid: string;
  storefront_project_name: string;
  rank: number;
  label: string;
  content_type: string;
}

interface MediaSmartlistSeasonTranslation {
  default: boolean;
  language: string;
  name: string;
  uuid: string;
}

interface MediaSmartlistSerieTranslation {
  default: boolean;
  language: string;
  smartlist_label: string;
  smartlist_uuid: string;
}

export interface MediaSmartlistSeason extends Omit<MediaSmartlist, "type"> {
  type: SmartlistType.SEASON;
  translations: MediaSmartlistSeasonTranslation[];
  serie_translations?: MediaSmartlistSerieTranslation[];
}

export type MediaSmartlistAdded = Omit<
  MediaSmartlist | MediaSmartlistSeason,
  "rank" | "content_type"
>;

export interface MediaTranslation {
  uuid: string;
  temp_pictures: unknown;
  slug: null | string;
  short_description: string;
  pictures: Pictures | null;
  picture: Picture | null;
  name: string;
  language: string;
  language_uuid: string;
  description: string;
  default: boolean;
}

export interface ChildClassification {
  uuid: string;
  key: string;
}

export interface MediaChildren {
  child_classification: ChildClassification;
  duration: number;
  duration_type: DurationType;
  freemium_end: string | null;
  freemium_end_ms: number | null;
  is_freemium: boolean;
  hosted_streams: HostedStream[];
  label: string;
  smartlists: MediaSmartlist[];
  translations: MediaTranslation[];
  translations_indexes: TranslationsIndexes;
  uuid: string;
}

export interface GetMediasMetadataListResponse {
  accessibilities: Accessibility[];
  categories: Category[];
  copyrights: Category[];
  duration_types: DurationType[];
  formats: KeyWithTranslation<MediaFormat>[];
  genres: Genre[];
  keywords: Category[];
  languages: Languages;
  nationalities: Nationality[];
  new_genres: KeyWithTranslation[];
  target_audiences: KeyWithTranslation[];
  themes: KeyWithTranslation[];
}

interface Accessibility {
  key: string;
  translations: Translation[];
  translations_indexes: TranslationsIndexes;
  uuid: string;
}

export interface Category {
  key: string;
  uuid: string;
}

export interface DurationType {
  duration: string;
  rank: number;
  uuid: string;
}

export interface Genre {
  key: string;
  legacy: boolean;
  uuid: string;
}

export interface Languages {
  data: Datum[];
  indexes: Indexes;
}

export interface Datum {
  id: string;
  part1: string;
  translations: Translation[];
  translations_indexes: TranslationsIndexes;
  uuid: string;
}

export interface GetMediasResponse {
  data: GetMediasItem[];
  indexes: Indexes;
  more: More;
  page: Page;
  type: string;
}

export interface MediasIndexes extends Indexes {
  publication_status: Record<MediaPublicationStatus, number[]>;
  status: Record<MediaStatus, number[]>;
}

export interface More {
  status_filter: MediaStatus[];
}

export interface GetMediasItem {
  duration: number;
  label: string;
  publication_status: MediaPublicationStatus;
  status: MediaStatus;
  type: MediaType;
  uuid: string;
  audio_only: boolean;
  is_replay: boolean;
  media_availability: string[];
  offers_count: number;
  begin_date?: string;
  live_end?: string | null;
}

export interface GetCastingListResponse {
  data: CastingListItem[];
  indexes: Indexes;
  page: Page;
  type: "casting";
}

export interface GetCastingListParams {
  page?: number;
  perPage?: number;
  search?: string | null;
  order?: { field: string; direction: string };
}

export interface CastingListItem {
  cover_picture_1_1: null | string;
  inserted_at: string;
  languages_count: number;
  medias_count: number;
  name: string;
  smartlists_count: number;
  slug: null | string;
  updated_at: string;
  uuid: string;
}

export interface GetCastingResponse {
  uuid: string;
  slug: string;
  custom_params: Record<string, string> | null;
  inserted_at: string;
  updated_at: string;
  isFetched: boolean;
  medias_castings: MediaCasting[];
  medias_castings_indexes: MediasCastingsIndexes;
  smartlists_castings: SmartlistCastingItem[];
  translations: CastingTranslation[];
  translations_indexes: TranslationsIndexes;
}

export interface SmartlistCastingItem {
  uuid: string;
  label: string;
  picture: string | null;
  type: SmartlistType;
  storefront_project_name: string;
  storefront_project_uuid: string;
}

export interface CastingTranslation {
  biography: string;
  signature: string;
  default: boolean;
  language: string;
  language_uuid: string;
  name: string;
  picture: Picture | null;
  pictures: Pictures | null;
  temp_pictures: null; // todo check
  uuid: string;
}

export interface CreateCastingTranslationResponse {
  uuid: string;
  default: boolean;
  language_uuid: string;
  name: null;
  biography: null;
  signature: null;
  casting_uuid: string;
}

export interface CastingTranslationToCreate {
  uuid?: string;
  default: boolean;
  language: string;
  language_uuid: string;
  name: string;
  biography: string;
  signature: string;
  picture?: Picture | null;
  pictures?: Pictures | null;
}

export interface CreateCastingTranslationParams {
  castingId: string;
  languageUuid: string;
}

export interface CreateCastingParams {
  uuid: string;
  data: GetCastingResponse;
}

export const CASTING_TYPE = {
  ACTOR: "actor",
  DIRECTOR: "director",
  PRODUCER: "producer",
  AUTHOR: "author",
  SALE: "sale",
  CONTACT: "contact",
} as const;

export type CastingType = (typeof CASTING_TYPE)[keyof typeof CASTING_TYPE];

export interface MediaCasting {
  media_picture: Picture | null;
  media_uuid: string;
  media_type: MediaType;
  uuid: string;
  type: CastingType;
}

interface MediasCastingsIndexes {
  type: CastingTypeIndexes;
}

type CastingTypeIndexes = Record<CastingType, number[]>;

export interface FastAsset {
  uuid: string;
  hls_status: EncodingStatus;
  mp4_status: EncodingStatus;
  hls_encoding_progression: number | null;
  mp4_encoding_progression: number | null;
  framerate: number | null;
}

interface Element {
  key: string;
  uuid: string;
}

export interface CreateKeywordResponse {
  categories: Element[];
  copyrights: Element[];
  genres: Element[];
  keywords: Element[];
}

export interface CreateTranslationParams {
  mediaId: string;
  languageId: string;
}

export interface CreateTranslationResponse {
  uuid: string;
}

export interface UploadPictureParams {
  itemId: string;
  pictureFile: File;
  translationId: string;
  pictureFormat: PictureKeys;
  contentType?: "medias" | "castings";
}

export interface DeletePictureParams {
  itemId: string;
  translationId: string;
  contentType?: "medias" | "castings";
}

export interface UploadPictureResponse {
  picture: Picture;
  picture_tags: PictureTag[];
  pictures: Pictures;
  temp_pictures: Picture;
}

export interface PictureTag {
  label: string;
  picture_tags_category: PictureTagsCategory;
  uuid: string;
}

export interface PictureTagsCategory {
  label: string;
  uuid: string;
}

export interface GetMediaFastAssetsResponse {
  fast_assets: FastAsset | null;
}

export interface CreateMediaResponse {
  uuid: string;
}

export interface DeleteMediaPictureTagResponse {
  uuid: string;
}

export interface UpdateMediaPictureTagResponse {
  uuid: string;
}

export interface UpdateMediaPictureTagParams {
  tagId: string;
  tagValue: string;
  translationId: string;
  ratioId: PictureKeys;
  itemType: "castings" | "medias";
}

export interface MediaWithTrailers {
  label: string;
  uuid: string;
}
export interface MediaWithTrailersItem {
  label: string;
  trailers: MediaWithTrailers[];
  uuid: string;
}

export type GetMediasWithTrailersListResponse = MediaWithTrailersItem[];

export interface GetIndexMediaAvailableListResponse {
  data: IndexMedia[];
  indexes: MediasIndexes;
  page: Page;
  type: "media";
}

export interface UpdateMediaResponse {
  uuid: string;
}

export interface UpdateMediaParams {
  data: GetMediaResponse;
  uuid: string;
}

export interface GetMediaStatusParams {
  uuid: string;
  projectSlug: string;
}

export interface GetMediaStatusResponse {
  label: string;
  status: string;
  uuid: string;
  publication_reason: string[];
  publication_status: string;
  is_archivable: boolean;
  is_deletable: boolean;
  is_editable: boolean;
}

export interface GetMediaFastAssetsPreviewResponse {
  framerate: number;
  type: string;
  url: string;
}

export type ValidateEpgSeverity = "error" | "warning" | "info";

export interface ValidateEpgFinding {
  code: string;
  count: number;
  samples: string[];
  severity: ValidateEpgSeverity;
}

export interface ValidateEpgSource {
  channels: number;
  programmes: number;
}

export interface ValidateEpgResponse {
  valid: boolean;
  findings: ValidateEpgFinding[];
  source?: ValidateEpgSource;
}
